import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MeasurementImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId?: string;
  tradeCode: string;
  orgId: string;
  onSuccess: () => void;
}

export function MeasurementImportDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  tradeCode,
  orgId,
  onSuccess,
}: MeasurementImportDialogProps) {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }

    setUploading(true);

    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove data:application/pdf;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call edge function to parse measurement report
      const { data, error } = await supabase.functions.invoke("parse-measurement-report", {
        body: {
          file_data: base64,
          file_name: file.name,
          job_id: jobId,
          trade_id: tradeId,
          trade_code: tradeCode,
          org_id: orgId,
        },
      });

      if (error) throw error;

      toast.success("Measurement report uploaded and parsed successfully!");
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload measurement report");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Measurement Report</DialogTitle>
          <DialogDescription>
            Upload a measurement report from Hover, GAF, Roofr, or other services
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Upload Button */}
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-primary" />
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold mb-1">Upload PDF Report</h3>
              <p className="text-sm text-muted-foreground">
                Supports Hover, GAF Roof Hub, Roofr, and other measurement PDFs
              </p>
            </div>

            <label htmlFor="file-upload">
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                className="cursor-pointer"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose PDF File
                  </>
                )}
              </Button>
              <input
                id="file-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>

          {/* Supported Services */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Supported Services:</p>
            <div className="grid grid-cols-3 gap-2">
              <div className="text-sm p-2 border rounded text-center">
                Hover
              </div>
              <div className="text-sm p-2 border rounded text-center">
                GAF Roof Hub
              </div>
              <div className="text-sm p-2 border rounded text-center">
                Roofr
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
